import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ChevronLeft } from "lucide-react";
import { useSale } from "./hooks";
import { hexaToDec } from "../../../utils/nfcHexaTranslate";
import {
  getCardNumbersFormat,
  getQrTransactionInfo,
  productTableConfig,
} from "./utils";
import Table from "../../../components/Core/Tables/Table";
import { EmptyData } from "../../../components/Core/EmptyData/EmptyData";
import LoaderPage from "../../../components/LoaderPage/LoaderPage";
import {
  PartialCancellationModal,
  SaleHeader,
  SaleSummary,
  PaymentDetail,
} from "./components";
import { CancellationModal } from "../../../components/CancellationModal/CancellationModal";
import { WarningModal } from "../../../components/WarningModal/WarningModal";
import { Input, Select } from "../../../components/Core/Form/component";
import { ErrorModalContext } from "../../../components/AlertsModal/AlertsModal";
import { textHasNoLetters } from "../../../utils/validations";
import { SaleStatus } from "../../../interfaces/sales";

const PAYMENT_TYPE_OPTIONS = [
  { name: "Tarjeta", path: "card" },
  { name: "Efectivo", path: "cash" },
] as const;

const DEFAULT_PAYMENT_TYPE = { label: "Tarjeta", value: "card" };

const TIP_INVALID_MESSAGE = "La propina solo puede contener números mayores o iguales a cero.";
const TIP_INVALID_TITLE = "Valor inválido";

const SaleDetail = () => {
  const { showAlert } = useContext(ErrorModalContext);
  const {
    cantRefund,
    isLoading,
    isLoadingRefund,
    isCompleting,
    makeRefund,
    completeSale,
    refreshData,
    sale,
    setShowRefundModal,
    showRefundModal,
    requiresSpei,
    cancelPhoneNumber,
    setCancelPhoneNumber,
    cardRefundTotal,
  } = useSale();
  const [showPartialCancelModal, setShowPartialCancelModal] = React.useState(false);
  const [showCompleteModal, setShowCompleteModal] = React.useState(false);
  const [completeModalTip, setCompleteModalTip] = React.useState("");
  const [completeModalPaymentType, setCompleteModalPaymentType] =
    React.useState<{ label: string; value: string }>(DEFAULT_PAYMENT_TYPE);

  const handleCloseCompleteModal = (): void => {
    setShowCompleteModal(false);
    setCompleteModalTip("");
    setCompleteModalPaymentType(DEFAULT_PAYMENT_TYPE);
  };

  /**
   * Blocks invalid key inputs for tip. Reuses same pattern as MinimumStockTab (blockedKeys +, -, e, E).
   */
  const handleTipKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    const blockedKeys = ["+", "-", "e", "E"];
    if (blockedKeys.includes(e.key)) {
      e.preventDefault();
      showAlert(TIP_INVALID_MESSAGE, "error", TIP_INVALID_TITLE);
    }
  };

  /** Same numeric-only regex used by Core/Form Input for type="number". */
  const numericOnlyRegex = /^[0-9]*\.?[0-9]*$/;

  const isTipValid = (value: string): boolean => {
    if (value === "") {
      return true;
    }
    return textHasNoLetters(value) && numericOnlyRegex.test(value) && !Number.isNaN(parseFloat(value)) && parseFloat(value) >= 0;
  };

  return isLoading ? (
    <LoaderPage />
  ) : sale ? (
    <div className="space-y-6">
      <CancellationModal
        closeModal={() => setShowRefundModal(false)}
        isLoading={isLoadingRefund}
        makeRefund={makeRefund}
        open={showRefundModal}
        orderCode={sale.code}
        requiresPhoneNumber={requiresSpei}
        cardRefundTotal={cardRefundTotal}
        onPhoneNumberChange={setCancelPhoneNumber}
      />
      <PartialCancellationModal
        open={showPartialCancelModal}
        closeModal={() => setShowPartialCancelModal(false)}
        products={sale.products}
        payments={sale.payments}
        saleUuid={sale.uuid}
        saleCode={sale.code}
        onSuccess={refreshData}
      />
      <SaleHeader
        canRefund={cantRefund.length === 0 && sale.status === SaleStatus.Pagada}
        code={sale.code}
        openRefundModal={() => setShowRefundModal(true)}
        openPartialCancelModal={() => setShowPartialCancelModal(true)}
        status={sale.status}
        onComplete={() => setShowCompleteModal(true)}
        bill={sale.bill}
      />
      <WarningModal
        open={showCompleteModal}
        onClose={handleCloseCompleteModal}
        title="Marcar como pagada"
        messageTitle="Advertencia importante"
        message={
          <p>
            Marcar la orden <span className="font-bold text-black">{sale.code}</span> como pagada impactará los reportes y las ventas del cliente y la concesión.
          </p>
        }
        additionalContent={
          <>
            <Input
              type="number"
              label="Propina"
              placeholder="0"
              value={completeModalTip}
              onChange={setCompleteModalTip}
              onKeyDown={handleTipKeyDown}
              min={0}
            />
            <Select
              label="Medio de pago"
              items={[...PAYMENT_TYPE_OPTIONS]}
              value={completeModalPaymentType}
              onChange={(option) => {
                if (option != null && typeof option === "object" && "label" in option && "value" in option) {
                  setCompleteModalPaymentType({ label: option.label, value: option.value });
                }
              }}
              placeholder="Selecciona medio de pago"
            />
          </>
        }
        confirmLabel="Confirmar acción"
        ConfirmIcon={CheckCircle2}
        onConfirm={async () => {
          if (!isTipValid(completeModalTip)) {
            showAlert(TIP_INVALID_MESSAGE, "error", TIP_INVALID_TITLE);
            return;
          }
          const tip = parseFloat(completeModalTip) || 0;
          const paymentType = completeModalPaymentType?.value === "cash" ? "cash" : "card";
          await completeSale({ tip, paymentType });
          handleCloseCompleteModal();
        }}
        isLoading={isCompleting}
      />
      <SaleSummary
        concession={sale.concession.name}
        date={sale.sold_at}
        event={sale.event.name}
        seller={sale.seller.name}
        subtotal={sale.subtotal}
        tip={sale.tip}
        total={sale.total}
      />
      {sale.comments && sale.comments.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-medium text-gray-800">
            {sale.comments.length === 1 ? 'Comentario' : 'Comentarios'}
          </h2>
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            {sale.comments.map((comment, index) => (
              <p key={index} className="text-sm text-gray-700">{comment}</p>
            ))}
          </div>
        </div>
      )}
      <div className="space-y-3">
        <h2 className="text-lg font-medium text-gray-800">Productos</h2>
        <Table
          columns={productTableConfig}
          data={sale.products}
        />
      </div>
      {sale.payments.length > 0 && (
        <div className="flex flex-col gap-3">
          <h2 className="text-lg font-medium text-gray-800">Pagos</h2>
          <div className="space-y-5">
            {sale.payments.map((item) => {
              return (
                <div
                  key={item.uuid}
                  className="rounded-xl border border-gray-200 bg-white"
                >
                  {item.nfc_transaction && (
                    <PaymentDetail
                      title="Pago con Tarjeta Cashless"
                      code={item.code}
                      total={item.total}
                      status={item.status}
                      fields={{
                        left: [
                          {
                            label: "Fecha",
                            value: item.paid_at,
                            type: "date",
                          },
                          {
                            label: "Número de la Tarjeta",
                            value: `${hexaToDec(
                              item.nfc_transaction.nfc_card.serial_number,
                            )} / ${
                              item.nfc_transaction.nfc_card.serial_number
                            }`,
                          },
                          {
                            label: "Saldo Pre-venta",
                            value: item.nfc_transaction.previous_balance,
                            type: "money",
                          },
                          {
                            label: "Subtotal",
                            value: item.subtotal,
                            type: "money",
                          },
                        ],
                        right: [
                          {
                            label: "ID de Pago",
                            value: item.uuid,
                            isCopyable: true,
                          },
                          {
                            label: "Identificador de Terminal",
                            value: "-",
                            isCopyable: true,
                          },
                          {
                            label: "Saldo Post-venta",
                            value: item.nfc_transaction.post_balance,
                            type: "money",
                          },
                          {
                            label: "Propina",
                            value: item.tip,
                            type: "money",
                          },
                        ],
                      }}
                    />
                  )}
                  {item.card_transaction && (
                    <PaymentDetail
                      title="Pago con Tarjeta Bancaria"
                      code={item.code}
                      total={item.total}
                      status={item.status}
                      fields={{
                        left: [
                          {
                            label: "Fecha",
                            value: item.paid_at,
                            type: "date",
                          },
                          {
                            label: "Marca",
                            value: item.card_transaction.card.brand,
                          },
                          {
                            label: "Banco",
                            value: item.card_transaction.card.bank,
                          },
                          {
                            label: "Número de la Tarjeta",
                            value: getCardNumbersFormat(
                              item.card_transaction.card.bin,
                              item.card_transaction.card.last_four,
                            ),
                            type: "cardNumber",
                          },
                          {
                            label: "Tipo de tarjeta",
                            value: item.card_transaction.card.type,
                          },
                          {
                            label: "Medio",
                            value: item.card_transaction.entry_mode,
                          },
                          {
                            label: "Subtotal",
                            value: item.subtotal,
                            type: "money",
                          },
                        ],
                        right: [
                          {
                            label: "ID de Pago",
                            value: item.uuid,
                            isCopyable: true,
                          },
                          {
                            label: "Identificador de Terminal",
                            value: item.card_transaction.terminal,
                            isCopyable: true,
                          },
                          {
                            label: "Código de Autorización",
                            value: item.card_transaction.authorization,
                            info: "Código único generado por el banco emisor que confirma la autorización de la transacción",
                          },
                          {
                            label: "Código de Operación",
                            value: item.card_transaction.operation_key,
                            info: "Identificador único de la operación bancaria que facilita el seguimiento de la transacción",
                          },
                          {
                            label: "Código de Transacción",
                            value: item.card_transaction.transaction_id,
                            info: "Número de referencia asignado por el procesador de pagos para identificar la transacción en su sistema",
                          },
                          {
                            label: "Código de Venta",
                            value: item.card_transaction.sale_key,
                          },
                          {
                            label: "Reembolso",
                            value: item.card_transaction.has_refund ? "Sí" : "",
                            isConditional: true,
                          },
                          {
                            label: "Razón de rechazo",
                            value: item.card_transaction.rejection_reason,
                            isConditional: true,
                          },
                          {
                            label: "Propina",
                            value: item.tip,
                            type: "money",
                          },
                        ],
                      }}
                    />
                  )}
                  {item.qr_transaction && (
                    <PaymentDetail
                      title="Pago con QR"
                      code={item.code}
                      total={item.total}
                      status={item.status}
                      fields={{
                        left: [
                          {
                            label: "Fecha",
                            value: item.paid_at,
                            type: "date",
                          },
                          {
                            label: item.qr_transaction.qr_ticket.uuid
                              ? "Ticket"
                              : "Cliente",
                            value: getQrTransactionInfo(item.qr_transaction),
                            isCopyable: !!item.qr_transaction.qr_ticket.uuid,
                          },
                          {
                            label: "Subtotal",
                            value: item.subtotal,
                            type: "money",
                          },
                        ],
                        right: [
                          {
                            label: "ID de Pago",
                            value: item.uuid,
                            isCopyable: true,
                          },
                          {
                            label: "Identificador de Terminal",
                            value: item.qr_transaction.terminal,
                            isCopyable: true,
                          },
                          {
                            label: "Propina",
                            value: item.tip,
                            type: "money",
                          },
                        ],
                      }}
                    />
                  )}
                  {item.palm_transaction && (
                    <PaymentDetail
                      title="Pago con Mano"
                      code={item.code}
                      total={item.total}
                      status={item.status}
                      fields={{
                        left: [
                          {
                            label: "Fecha",
                            value: item.paid_at,
                            type: "date",
                          },
                          {
                            label: "Cliente",
                            value: item.palm_transaction.palm_fingerprint?.customer?.phone_number || "-",
                            isCopyable: true,
                          },
                          {
                            label: "Subtotal",
                            value: item.subtotal,
                            type: "money",
                          },
                        ],
                        right: [
                          {
                            label: "ID de Pago",
                            value: item.uuid,
                            isCopyable: true,
                          },
                          {
                            label: "Identificador de Terminal",
                            value: item.palm_transaction.terminal,
                            isCopyable: true,
                          },
                          {
                            label: "Propina",
                            value: item.tip,
                            type: "money",
                          },
                        ],
                      }}
                    />
                  )}
                  {item.cash_transaction && (
                    <PaymentDetail
                      title="Pago en Efectivo"
                      code={item.code}
                      total={item.total}
                      status={item.status}
                      fields={{
                        left: [
                          {
                            label: "Fecha",
                            value: item.paid_at,
                            type: "date",
                          },
                          {
                            label: "Subtotal",
                            value: item.subtotal,
                            type: "money",
                          },
                        ],
                        right: [
                          {
                            label: "ID de Pago",
                            value: item.uuid,
                            isCopyable: true,
                          },
                          {
                            label: "Identificador de Terminal",
                            value: "-",
                            isCopyable: true,
                          },
                          {
                            label: "Propina",
                            value: item.tip,
                            type: "money",
                          },
                        ],
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  ) : (
    <>
      <Link
        to="/panel/ordenes"
        className="flex w-fit items-center gap-1 text-gray-500 transition-colors duration-300 hover:text-gray-900"
      >
        <ChevronLeft className="h-4 w-4" />
        <span className="text-sm">Volver a Órdenes</span>
      </Link>
      <EmptyData
        title="No se encontró información de la venta"
        description="Intenta nuevamente con otra venta"
      />
    </>
  );
};

export default SaleDetail;
